import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import { Controller, useForm } from 'react-hook-form';
import Input from '../../components/Input/Input';
import { useDispatch, useSelector } from 'react-redux';
import { createUser } from '../../redux/actions/user/createUser';
import Loading from '../../components/Loading/Loading';
import Swal from 'sweetalert2';
import { resetCreateUser, resetUpdateUserPermissions } from '../../redux/slices/user.slice';
import { createEmployer } from '../../redux/actions/employer/createEmployer';
import { resetCreateEmployer } from '../../redux/slices/employer.slice';
import PermissionsCheckboxList from '../../components/Permissions/PermissionsCheckboxList';
import { updateUserPermissions } from '../../redux/actions/user/updateUserPermissions';
import { usePermissions } from '../../hooks/usePermissions';
import AccessDenied from '../../components/AccessDenied/AccessDenied';
import { PERMISSIONS } from '../../constants/permissions';

function AddUser() {
  const { role } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { hasPermission, isSuperuser, isLoaded } = usePermissions();
  const [createdUserId, setCreatedUserId] = useState(null);

  const {
    createUser: { data: createUserData, error: createUserError, loading: createUserLoading },
    updateUserPermissions: { data: updatePermData, loading: updatePermLoading },
  } = useSelector((state) => state.user);
  const {
    createEmployer: { data: createEmployerData, error: createEmployerError, loading: createEmployerLoading },
  } = useSelector((state) => state.employer);

  const form = useForm({
    defaultValues: {
      email: '',
      fio: '',
      type: 'ie',
      permissions: {},
    },
  });

  useEffect(() => {
    if (role !== 'admin' && role !== 'manager' && role !== 'user') {
      navigate('/');
    }
  }, [role, navigate]);

  const onSubmit = (data) => {
    if (role === 'user') {
      dispatch(
        createEmployer({
          name: data.fio,
          email: data.email,
          type: data.type,
        }),
      );
    } else {
      dispatch(
        createUser({
          full_name: data.fio,
          email: data.email,
          ...(role === 'admin' ? { type: 'staff', role: 'admin' } : { type: 'staff', role: 'manager' }),
        }),
      );
    }
  };

  // После создания пользователя - обновляем полномочия
  useEffect(() => {
    if (createUserData && !createdUserId) {
      setCreatedUserId(createUserData.data.id);

      // Формируем массив полномочий для API
      const permissionsData = form.getValues('permissions');
      let permissionsArray = Object.entries(permissionsData)
        .filter(([_, checked]) => checked)
        .map(([name]) => ({ name }));

      if (permissionsArray.length > 0) {
        permissionsArray.push({ name: 'view_user' });
        if (permissionsArray.find(({ name }) => name == 'add_manager')) {
          permissionsArray.push({ name: 'add_user' });
          permissionsArray.push({ name: 'change_user_permissions' });
          permissionsArray.push({ name: 'view_permissions' });
        }
        dispatch(
          updateUserPermissions({
            userId: createUserData.data.id,
            permissions: permissionsArray,
          }),
        );
      } else {
        // Если полномочий нет - сразу показываем успех
        showSuccessAndReset();
      }
    }
  }, [createUserData]);

  // После обновления полномочий - показываем успех
  useEffect(() => {
    if (updatePermData && createdUserId) {
      showSuccessAndReset();
    }
  }, [updatePermData]);

  const showSuccessAndReset = () => {
    Swal.fire({
      title: 'Пользователь создан. Пароль отправлен на почту пользователя',
      showConfirmButton: false,
      timer: 2000,
      timerProgressBar: true,
    });
    form.reset();
    setCreatedUserId(null);
    dispatch(resetCreateUser());
    dispatch(resetUpdateUserPermissions());
  };

  useEffect(() => {
    if (createEmployerData) {
      Swal.fire({
        title: 'Контрагент создан. Пароль отправлен на почту пользователя',
        showConfirmButton: false,
        timer: 2000,
        timerProgressBar: true,
      });
      form.reset();
      dispatch(resetCreateEmployer());
    }
  }, [createEmployerData]);

  const types = [
    { label: 'ИП', value: 'ie' },
    { label: 'Организация', value: 'company' },
    { label: 'Физическое лицо', value: 'individual' },
  ];

  const typeWatch = form.watch('type');
  const isLoading = createUserLoading || createEmployerLoading || updatePermLoading;
  const showPermissions = role === 'admin' || role === 'manager';
  // Проверка доступа для создания админа - только superuser
  if (isLoaded && role === 'admin' && !isSuperuser) {
    return <AccessDenied />;
  }
  // Проверка доступа для создания менеджера
  if (isLoaded && role === 'manager' && !hasPermission(PERMISSIONS.ADD_MANAGER)) {
    return <AccessDenied />;
  }
  return (
    <div className="w-full flex flex-1 mt-10 p-1">
      <div className="container relative flex flex-1 flex-col mx-auto max-w-[1163px]">
        <div className="flex flex-col sm:flex-row w-full gap-3">
          <div className="flex w-full sm:w-[20%] mb-2 justify-center">
            <div className="bg-[#E5E5E5] w-[160px] h-[160px] m-5 flex items-center justify-center rounded-lg">
              <svg xmlns="http://www.w3.org/2000/svg" width="64px" height="64px" viewBox="0 0 24 24">
                <path fill="rgba(0,0,0,0.6)" d="M12 4a4 4 0 0 1 4 4a4 4 0 0 1-4 4a4 4 0 0 1-4-4a4 4 0 0 1 4-4m0 10c4.42 0 8 1.79 8 4v2H4v-2c0-2.21 3.58-4 8-4" />
              </svg>
            </div>
          </div>
          <div className="flex flex-col w-full sm:w-[50%] mb-2 p-5 gap-3">
            <h2 className="text-[25px] font-normal text-black">Добавить {role === 'user' ? 'контрагента' : role === 'admin' ? 'админа' : role === 'manager' ? 'менеджера' : ''}</h2>

            {role === 'user' && (
              <fieldset className="flex w-full first:flex-row xs:flex-col gap-4 mb-1">
                <legend className="mb-4 text-[14px]">Тип пользователя</legend>
                <Controller
                  control={form.control}
                  name="type"
                  render={({ field: { onChange, ...props } }) =>
                    types.map((option) => (
                      <div className="relative flex gap-x-3" key={option.value}>
                        <div className="flex h-6 items-center">
                          <input type="radio" id={option.value} {...props} onChange={onChange} value={option.value} checked={typeWatch === option.value} className="h-4 w-4 rounded border-gray-300 text-blue-500 focus:ring-blue-500" />
                        </div>
                        <div className="text-sm leading-6">
                          <label htmlFor={option.value} className="font-medium text-gray-900">
                            {option.label}
                          </label>
                        </div>
                      </div>
                    ))
                  }
                />
              </fieldset>
            )}

            <div className="">
              <Input form={form} name="fio" placeholder="ФИО" disabled={isLoading} />
            </div>
            <Input
              form={form}
              name="email"
              placeholder="Email"
              rules={{
                required: { value: true, message: '*Обязательное поле для заполнения' },
                pattern: {
                  value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                  message: 'Неверный формат почты',
                },
              }}
              disabled={isLoading}
            />

            {/* Чекбоксы полномочий */}
            {showPermissions && <PermissionsCheckboxList form={form} name="permissions" disabled={isLoading} />}

            <div className="flex flex-row gap-3 relative">
              <button onClick={form.handleSubmit(onSubmit)} type="button" className="text-center border rounded py-2 px-4 bg-button text-white hover:bg-button-hov mr-2 w-full md:w-auto transition ease-in-out duration-300" disabled={isLoading}>
                Сохранить
              </button>
              <button onClick={() => navigate(-1)} type="button" className="text-center border rounded py-2 px-4 text-button border-button border-1 w-full md:w-auto hover:text-white bg-white hover:bg-button transition ease-in-out duration-300">
                Отмена
              </button>
              {isLoading && (
                <div className="translate-x-5">
                  <Loading />
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default AddUser;
